import { type ReactElement } from 'react';
import { Chip, Stack } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { type SelectedChip } from './ContentItemEditor.types';

type Props = {
    items: SelectedChip[];
    // Left out when the field is read-only, which removes the delete control from every chip.
    onRemove?: (key: string) => void;

    // 'column' gives each value its own row, which reads as a list of that field's values and suits
    // a field that holds a few. 'wrap' flows the chips inline, for a field that routinely holds
    // dozens: a course sits in as many access groups as the curriculum needs, and one row each ran
    // to 2000 pixels.
    layout?: 'column' | 'wrap';
};

/*
    The values chosen in a picker, listed below its input rather than as chips inside it. The input
    then stays one line however many values are attached.

    One column, and no wider than the input above it, so a list reads as the values of that field.
    A label too long for the column truncates into its tooltip.

    The parent owns the space above the list.
*/
export default function SelectedChipList({ items, onRemove, layout = 'column' }: Props): ReactElement | null {
    if (items.length === 0) return null;

    return (
        <Stack
            direction={layout === 'wrap' ? 'row' : 'column'}
            spacing={1}
            useFlexGap
            sx={{
                alignItems: 'flex-start',
                flexWrap: layout === 'wrap' ? 'wrap' : 'nowrap',
            }}
        >
            {items.map(item => {
                const linkProps = item.href
                    ? ({
                          component: 'a',
                          href: item.href,
                          target: '_blank',
                          rel: 'noreferrer',
                          clickable: true,
                          icon: <OpenInNew fontSize="small" />,
                      } as const)
                    : {};

                return (
                    <Chip
                        key={item.key}
                        label={item.label}
                        title={item.label}
                        color={item.color}
                        {...linkProps}
                        // The pill holds its content width until the label reaches the column edge,
                        // where it truncates instead of overflowing.
                        sx={{ maxWidth: '100%' }}
                        onDelete={
                            onRemove &&
                            (event => {
                                // MUI's delete handler only stops propagation, so without
                                // preventDefault the browser also follows a chip that is a link.
                                event.preventDefault();
                                onRemove(item.key);
                            })
                        }
                    />
                );
            })}
        </Stack>
    );
}
